import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import {
  renderAdventurerSilhouette,
  renderArmorSlotIcon,
  renderBackpackIcon,
  renderHandSlotIcon,
  renderTwoHandSlotIcon,
} from '../shared/icons.js';
import '../shared/app-viewport.js';
import { api } from '../../lib/api.js';
import {
  isHelpMessageDismissed,
  isHelpMessageSuppressedInGame,
  setHelpMessageDismissed,
  suppressHelpMessageInGame,
} from '../../lib/help-state.js';
import {
  alternateEquipOption,
  decideEquipPath,
  itemAcceptsSlot,
  noEquipmentChangesLeft,
} from './equipment-screen-logic.js';

/** Stable ids used by the help-state API for the dismissible help banners
 *  rendered by this screen. Add a new one + a default-state pair (see
 *  `_dismissibleHelp` below) when introducing another banner. */
const COST_EXPLAINER_HELP_ID = 'equipment-cost-explainer';
const DRAG_INSTRUCTION_HELP_ID = 'equipment-drag-instruction';

/**
 * @typedef {{ equipmentId?: string, itemIndex?: number, slot: string, equip: boolean }} EquipmentChange
 * @typedef {{ changes: EquipmentChange[] }} PendingSwap
 */

/**
 * Near-full-screen equipment management modal for #232 / #313.
 *
 * Drag an inventory card onto a slot to trigger an equip flow. The modal
 * surfaces only the tiers (Object Interaction / Bonus / Action / free
 * Attack-bundled) the character can still afford this turn — the chips
 * update from server-authoritative `character.equipmentBudget`. Out of
 * combat the chip row collapses and swaps are unlimited.
 *
 * The free Attack-bundled swap is staged client-side and committed when
 * the player submits the next attack (`request-pending-swap` event lifts
 * the staged change up to <game-map> which sends it as
 * `prefixEquipmentChanges` on the next pc:act:attack). Cancel reverts.
 */
class EquipmentScreen extends BaseComponent {
  static properties = {
    character: { type: Object },
    pendingSwap: { type: Object },
    combatTime: { type: Boolean },
    isMyTurn: { type: Boolean },
    /** Stable game id used for per-game help-state suppression. */
    gameId: { type: String, attribute: 'game-id' },
    _hoverSlot: { state: true },
    /**
     * Tagged union describing the in-flight drag source:
     *   - { kind: 'inventory', index }: dragging a card from My Equipment
     *     onto a slot (equip flow)
     *   - { kind: 'slot', slot, equipmentId, itemIndex }: dragging an
     *     equipped item out of its slot (unequip flow)
     * null when nothing is being dragged.
     */
    _dragSource: { state: true },
    _equipDecision: { state: true },
    _attackPicker: { state: true },
    // _unequipConfirm: set when the user has dropped an equipped item
    // into the inventory pane and we need their tier choice; renders
    // inline as the drop-zone confirm. Field type lives on the
    // constructor initialiser.
    _unequipConfirm: { state: true },
    _shakeIndex: { state: true },
    /**
     * Per-helpId state for the dismissible help banners on this screen.
     * Shape: { [helpId]: { dismissed: boolean, hidden: boolean, dontShowAgain: boolean } }
     * - dismissed → server-persisted "don't show again" already true.
     * - hidden    → user clicked Dismiss this mount (session/per-game suppression).
     * - dontShowAgain → mirror of the per-banner checkbox.
     * Default-dismissed-on-construct so banners don't flash before the
     * help-state API resolves.
     */
    _help: { state: true },
  };

  constructor() {
    super();
    /** @type {any} */
    this.character = null;
    /** @type {PendingSwap | null} */
    this.pendingSwap = null;
    this.combatTime = false;
    this.isMyTurn = false;
    this.gameId = '';
    // Default to dismissed=true so banners don't flash before the
    // help-state API resolves; connectedCallback flips them on if the
    // user hasn't dismissed.
    this._help = {
      [COST_EXPLAINER_HELP_ID]: { dismissed: true, hidden: false, dontShowAgain: false },
      [DRAG_INSTRUCTION_HELP_ID]: { dismissed: true, hidden: false, dontShowAgain: false },
    };
    /** @type {string | null} */
    this._hoverSlot = null;
    /** @type {{ kind: 'inventory', index: number } | { kind: 'slot', slot: string, equipmentId: string, itemIndex: number } | null} */
    this._dragSource = null;
    /**
     * Drop decision currently awaiting user choice (rendered as the
     * "Equip with Attack?" / "Unequip with Attack?" modal). null when no
     * decision is pending. `equip` distinguishes the two flows; for an
     * unequip there's no replaced item, so those fields stay null.
     * @type {{
     *   slot: string,
     *   itemIndex: number,
     *   equipmentId: string,
     *   equip: boolean,
     *   replacedItemIndex: number | null,
     *   replacedEquipmentId: string | null,
     * } | null}
     */
    this._equipDecision = null;
    /**
     * After the player picks "Equip with Attack", we transition to the
     * attack-action picker so they can pick which attack to bundle the
     * swap with. null when not in that state.
     *
     * `previewCharacter` is filled async by the server's preview-actions
     * endpoint — null while loading; the full recomputed character record
     * (actions, targetSquares, equipmentBudget, ac, …) on success; null +
     * a `previewError` string on failure. The picker renders both branches
     * so the player can choose pre or post swap, and the picked branch's
     * preview record is stashed on `_pendingEquipmentSwap` so game-map can
     * overlay it as `myChar` while the swap is staged.
     * @type {{ changes: EquipmentChange[], previewCharacter: any | null, previewError: string | null } | null}
     */
    this._attackPicker = null;
    /** @type {{ slot: string, itemIndex: number, equipmentId: string } | null} */
    this._unequipConfirm = null;
    /** @type {number | null} */
    this._shakeIndex = null;
  }

  static styles = css`
    :host {
      display: contents;
      /* Opt out of the MedievalSharp heading font app-viewport applies by
         default — the equipment manager wants a plain system heading.
         Mirrors Bootstrap's --bs-font-sans-serif stack with hard-coded
         fallbacks since Bootstrap's vars don't pierce shadow boundaries. */
      --app-viewport-heading-font:
        system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue',
        'Noto Sans', 'Liberation Sans', Arial, sans-serif;
    }

    /* app-viewport gives us the full-screen white modal panel and the
       Equipment Manager header. Everything below is layout flowing
       inside its body — no fixed pane sizes, just rows and columns. */
    app-viewport {
      --av-body-padding: 0;
    }

    /* Outermost layout primitives, all flow-based:
       .page         — horizontal split: left = cost + slots, right = inventory
       .col          — vertical column with consistent gap
       .section      — labelled block (subheader + content)
       .subheader    — h2-sized section title
       .row          — horizontal flex row with gap
       Items inside size to their own content; columns share remaining
       horizontal space via flex: 1 (with min-width: 0 so flex-children
       can shrink properly when content is wide). */

    /* The top-level split uses Bootstrap's grid (BaseComponent adopts
       Bootstrap into every shadow root) — the .page is a Bootstrap
       .row, columns are .col-md-* / .col-xl-* so the inventory side
       grows on wider viewports. .pane just adds visual styling on top
       of the Bootstrap-sized columns. */
    .page {
      flex: 1;
      min-height: 0;
    }
    /* The .pane class sits on the same element as the Bootstrap col-*
       classes — it adds the column's vertical layout (flex column with
       gap, padding) on top of the col's width sizing. min-width: 0 so
       the col doesn't expand past its allotment when content (slots
       grid / inventory grid) is wide. */
    .pane {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 1.25rem;
      padding: 1rem 1.25rem;
      min-width: 0;
      min-height: 0;
      overflow-y: auto;
      /* Reserve scrollbar gutters on both inline edges. "stable" alone
         reserves only the inline-end (right for LTR), which creates a
         visible asymmetry between left and right padding. "both-edges"
         keeps them balanced whether or not the list actually overflows. */
      scrollbar-gutter: stable both-edges;
    }
    .pane.left {
      background: #f8f9fa;
      border-right: 1px solid #dee2e6;
    }

    /* Sections use Bootstrap's d-flex flex-column gap-2 utilities
       inline — no custom .section class needed. */

    .subheader {
      margin: 0;
      font-size: 1.5rem;
      font-weight: 600;
      color: #212529;
    }

    /* No custom .row rule here — Bootstrap's .row class supplies the
       grid behavior, and the per-element customisations live on the
       specific element (e.g. .page) so they don't leak. */

    /* Pending-swap banner and the info cards both use Bootstrap's
       .alert classes — no custom styling here. The cost-indicator
       card uses Bootstrap .card / .card-body. */

    /* Change-cost indicators: vertical pip+label rows inside a
       Bootstrap card. The pip itself is a custom 24px circle that
       mirrors the turn-bar resource pip (filled green when spent,
       outlined when available). */
    .cost-indicator {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      font-size: 0.875rem;
      color: #212529;
    }
    .cost-indicator .pip {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      border: 2px solid #adb5bd;
      background: #ffffff;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 13px;
      font-weight: 700;
      color: #ffffff;
      flex-shrink: 0;
    }
    .cost-indicator.available .pip {
      border-color: #6c757d;
    }
    .cost-indicator.used .pip {
      border-color: #6c757d;
      background: #6c757d;
    }
    .cost-indicator.used .label {
      text-decoration: line-through;
      color: #6c757d;
    }
    .cost-indicator.no-attack-left {
      opacity: 0.55;
    }

    /* Inline-text variant of the cost-indicator pip — used in the help
       banner so the prose can show example glyphs at a smaller, line-
       height-friendly size. Same color logic as the full-size pip. */
    .cost-indicator-pip-inline {
      width: 1em;
      height: 1em;
      border-radius: 50%;
      border: 2px solid #6c757d;
      background: #ffffff;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      vertical-align: -0.15em;
      font-size: 0.75em;
      font-weight: 700;
      color: #ffffff;
    }
    .cost-indicator-pip-inline.used {
      background: #6c757d;
    }

    /* Slots grid: 3 columns × 2 rows. The silhouette spans both rows
       in the middle column. Top-row slots align to their cell bottom
       (so they sit just above the figure's vertical midpoint, near the
       chest). Bottom-row slots align to their cell top (so they sit
       just below the midpoint, near the hands). The top-left cell is
       intentionally empty for now — leaves room for future slots. */
    .slots-grid {
      display: grid;
      grid-template-columns: auto 1fr auto;
      grid-template-rows: 1fr 1fr;
      gap: 1rem;
      align-items: stretch;
      justify-items: center;
      position: relative;
    }
    /* Mirror of .unequip-zone.blocked — covers the silhouette/slots while
       the player drags an inventory card during combat with every tier
       already spent. The dragover gate refuses preventDefault under the
       same condition so the browser snaps the card back. */
    .slots-grid .slots-blocked-overlay {
      position: absolute;
      inset: 0;
      z-index: 5;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      border: 2px solid #dc3545;
      border-radius: 10px;
      background: rgba(248, 215, 218, 0.85);
      color: #842029;
      pointer-events: none;
      text-align: center;
      padding: 0.75rem;
    }
    .slots-grid .slots-blocked-overlay i {
      font-size: 2rem;
      line-height: 1;
    }
    .slots-grid .slots-blocked-overlay .title {
      font-weight: 600;
      font-size: 1.125rem;
    }
    .silhouette-col {
      grid-column: 2;
      grid-row: 1 / 3;
      min-width: 0;
      display: flex;
      align-items: stretch;
      justify-content: center;
      width: 100%;
    }
    /* Slot grid placement + within-cell alignment. */
    .slot[data-slot='armor'] {
      grid-column: 3;
      grid-row: 1;
      align-self: end;
    }
    .slot[data-slot='mainHand'] {
      grid-column: 1;
      grid-row: 2;
      align-self: start;
    }
    .slot[data-slot='offHand'] {
      grid-column: 3;
      grid-row: 2;
      align-self: start;
    }
    /* Silhouette fills its column horizontally; height derives from
       the SVG's intrinsic aspect (viewBox + default preserveAspectRatio). */
    .silhouette-figure {
      color: #212529;
      opacity: 0.22;
    }
    .silhouette-figure svg {
      width: 100%;
      height: auto;
    }

    .slot {
      position: relative;
      width: 90px;
      height: 90px;
      border: 2px dashed #adb5bd;
      border-radius: 10px;
      background: #ffffff;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      padding: 0.375rem;
      transition:
        border-color 0.12s,
        background 0.12s,
        box-shadow 0.12s;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    }
    /* Label sits flush at the top of the slot; item name centers in the
       remaining space below. */
    .slot .slot-item-name {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    /* Solid blue border + light blue background for slots that hold an
       equipped item — distinguishes them from empty drop targets. */
    .slot.occupied {
      border-style: solid;
      border-color: #0d6efd;
      background: #e7f1ff;
    }
    .slot.valid-target {
      border-style: solid;
      border-color: #0d6efd;
      background: #cfe2ff;
      box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.15);
    }
    .slot.invalid-target {
      border-color: #dc3545;
      background: #f8d7da;
    }
    /* Off-hand showing the second grip of a two-handed weapon equipped in
       main-hand. Defined BEFORE the drag states below so an in-flight
       drag (drag-eligible / drag-blocked) overrides the grip styling —
       the player needs to see the slot's drop-eligibility regardless of
       what currently lives in it. */
    .slot.two-handed-grip {
      border-style: solid;
      border-color: #6ea8fe;
      background: #f0f6ff;
    }
    .slot.two-handed-grip .slot-item-name {
      color: #495057;
      flex-direction: column;
      gap: 0.125rem;
    }
    /* Persistent eligibility hint while an inventory drag is in flight.
       Dotted border + tinted fill softer than .valid-target /
       .invalid-target so an actual hover still amplifies the slot. */
    .slot.drag-eligible {
      border-style: dotted;
      border-color: #0d6efd;
      background: #e7f1ff;
    }
    .slot.drag-blocked {
      border-style: dotted;
      border-color: #dc3545;
      background: #f8d7da;
    }
    /* Hide whatever's currently equipped (or "Empty" placeholder text)
       while a drag is in flight so the eligibility overlay is the only
       thing competing for the player's eye. */
    .slot.drag-eligible .slot-item-name,
    .slot.drag-blocked .slot-item-name {
      visibility: hidden;
    }
    /* Overlay glyph that fills each slot during a drag — the icon of
       what would land here on success, or a red X when the drop is
       blocked. Absolutely positioned so it doesn't displace the slot's
       label / item-name layout. */
    .slot .drag-overlay {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.125rem;
      pointer-events: none;
      opacity: 0.7;
    }
    .slot .drag-overlay svg {
      width: 36px;
      height: 36px;
      display: block;
    }
    .slot .drag-overlay .bi {
      font-size: 36px;
      line-height: 1;
    }
    .slot .drag-overlay-reason {
      font-size: 0.625rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      line-height: 1;
    }
    .slot.drag-eligible .drag-overlay {
      color: #0d6efd;
      opacity: 0.55;
    }
    .slot.drag-blocked .drag-overlay {
      color: #dc3545;
    }
    .slot .slot-label {
      font-size: 0.625rem;
      color: #6c757d;
      text-transform: uppercase;
      letter-spacing: 0.08em;
    }
    .slot .slot-item-name {
      font-size: 0.75rem;
      color: #212529;
      text-align: center;
      font-weight: 500;
    }
    .slot.empty .slot-item-name {
      color: #adb5bd;
      font-style: italic;
    }
    .slot.two-handed-grip .slot-grip-note {
      margin-top: 0.125rem;
      font-size: 0.625rem;
      color: #6c757d;
      font-style: italic;
      font-weight: 400;
    }

    /* Slot grid placement is handled in the .slots-grid section above.
       Body in top-right (align bottom), main-hand in bottom-left
       (align top), off-hand in bottom-right (align top). Top-left
       cell is empty. */

    /* Inventory grid sits inside .col.right; auto-fill columns let
       cards reflow as the column resizes. */
    /* The unequip drop zone overlays the inventory area while a slot-
       sourced drag is in flight or a confirm is pending — the inventory
       cards stay rendered behind a semi-transparent dashed-blue panel,
       so the player can see what they're "above" while still having one
       clear target. Mirrors the dashed-blue dropzones used in the new-
       character flow. */
    .unequip-zone {
      position: absolute;
      inset: 0;
      margin: 1rem 1.25rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.75rem;
      padding: 2rem;
      border: 2px dashed #0d6efd;
      border-radius: 0.5rem;
      background: rgba(207, 226, 255, 0.85);
      backdrop-filter: blur(2px);
      color: #052c65;
      text-align: center;
      z-index: 5;
    }
    .unequip-zone i.bi {
      font-size: 3rem;
      line-height: 1;
    }
    .unequip-zone .title {
      font-weight: 600;
      font-size: 1.125rem;
    }
    /* Blocked variant — armor slot dragged during combat. Red-themed
       (Bootstrap danger palette) with an X glyph instead of the
       arrow, signaling "this drop will do nothing". */
    .unequip-zone.blocked {
      border-color: #dc3545;
      background: rgba(248, 215, 218, 0.85);
      color: #842029;
    }

    .inventory-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
      gap: 0.75rem;
    }

    .inv-card {
      background: #ffffff;
      border: 1px solid #dee2e6;
      border-radius: 0.625rem;
      padding: 0.75rem;
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
      cursor: grab;
      user-select: none;
      position: relative;
      transition:
        border-color 0.12s,
        transform 0.18s,
        box-shadow 0.18s;
    }
    .inv-card:hover {
      border-color: #adb5bd;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
    }
    .inv-card:active {
      cursor: grabbing;
    }
    /* Items with no equippable slots (gear, potions, ammunition, etc.)
       can't be dragged onto the silhouette, so suppress the grab cursor
       and active grabbing override. */
    .inv-card.not-equippable,
    .inv-card.not-equippable:active {
      cursor: default;
    }
    .inv-card.equipped {
      border-color: #0d6efd;
      background: #f0f6ff;
    }
    .inv-card.dragging {
      opacity: 0.4;
    }
    .inv-card.shake {
      animation: shake 0.4s ease;
    }
    @keyframes shake {
      0%,
      100% {
        transform: translateX(0);
      }
      25% {
        transform: translateX(-6px);
      }
      75% {
        transform: translateX(6px);
      }
    }

    /* Header row: name + (optional) quantity badge + info icon, sitting
       grouped on the left. Slot-requirement glyph is absolutely
       positioned in the top-right of the card and isn't part of this
       row's flow. */
    .inv-card .name-row {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      /* Make room for the absolutely-positioned slot glyph so the row
         doesn't slide under it. */
      padding-right: 2.5rem;
    }
    .inv-card .name {
      font-size: 0.875rem;
      font-weight: 600;
      color: #212529;
      min-width: 0;
    }
    /* Quantity badge — softer than the category badge so it doesn't
       compete with the item name. Outline + secondary text, no fill. */
    .inv-card .qty-badge {
      font-size: 0.6875rem;
      font-weight: 500;
      color: #6c757d;
      background: transparent;
      border: 1px solid #dee2e6;
      flex-shrink: 0;
    }
    /* Inline info "i" — sits to the right of the name + quantity badge
       and opens a Bootstrap tooltip with the SRD description on
       hover/focus. */
    .inv-card .item-info-icon {
      color: #6c757d;
      cursor: help;
      font-size: 0.875rem;
      flex-shrink: 0;
    }
    .inv-card .item-info-icon:hover,
    .inv-card .item-info-icon:focus {
      color: #0d6efd;
      outline: none;
    }
    /* Summary line — damage / AC info, smaller than the name. */
    .inv-card .summary {
      font-size: 0.75rem;
      color: #495057;
    }
    /* Badge row — category first, then weapon properties. */
    .inv-card .badge-row {
      display: flex;
      flex-wrap: wrap;
      gap: 0.25rem;
    }
    .inv-card .badge-row .badge {
      font-size: 0.6875rem;
      font-weight: 500;
    }
    /* Slot requirement glyph at the top-right of the card. Absolutely
       positioned so growing the icon doesn't displace the title or the
       meta row. Tinted in the same muted gray as the meta row so it
       reads as a label, not as a foreground element. */
    .inv-card .slot-icons {
      position: absolute;
      top: 0.5rem;
      right: 0.5rem;
      display: flex;
      gap: 0.25rem;
      color: #6c757d;
      pointer-events: none;
    }
    .inv-card .slot-icons svg {
      width: 27px;
      height: 27px;
      display: block;
    }
    .empty-text {
      color: #6c757d;
      font-style: italic;
      text-align: center;
      padding: 2.5rem;
    }

    /* Sub-modals (equip-decision + attack-picker). Layered above
       app-viewport (z-index 1040) so they cover the modal-panel and
       the player can't interact with the equipment screen behind. */
    .sub-modal-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.4);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1050;
    }
    .sub-modal {
      background: #ffffff;
      border: 1px solid #dee2e6;
      border-radius: 0.5rem;
      padding: 1.25rem;
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2);
      display: flex;
      flex-direction: column;
      gap: 0.875rem;
      min-width: 320px;
      max-width: min(560px, calc(100% - 32px));
    }
    .sub-modal-title {
      margin: 0;
      font-size: 1.125rem;
      font-weight: 600;
      color: #212529;
    }
    .sub-modal-body {
      margin: 0;
      font-size: 0.875rem;
      line-height: 1.5;
      color: #495057;
    }
    .sub-modal-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      justify-content: flex-end;
    }
    /* Sub-modal buttons + attack-list use Bootstrap's btn / list-group
       classes — no custom styling needed. The attack-list just gets a
       max-height so it scrolls instead of pushing the modal off-screen. */
    .attack-list {
      max-height: 320px;
      overflow-y: auto;
    }
  `;

  async connectedCallback() {
    await super.connectedCallback();
    // Resolve every help banner's persistent dismissal state in parallel.
    // Per-game session suppression (sessionStorage) is a sync check that
    // short-circuits the async lookup. Either one keeps the banner hidden.
    const ids = Object.keys(this._help);
    const next = { ...this._help };
    await Promise.all(
      ids.map(async (id) => {
        if (this.gameId && isHelpMessageSuppressedInGame(this.gameId, id)) {
          next[id] = { ...next[id], dismissed: true };
          return;
        }
        try {
          next[id] = { ...next[id], dismissed: await isHelpMessageDismissed(id) };
        } catch {
          next[id] = { ...next[id], dismissed: false };
        }
      }),
    );
    this._help = next;
  }

  updated() {
    this._initTooltips();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._disposeTooltips();
  }

  _initTooltips() {
    this._disposeTooltips();
    const Tooltip = /** @type {{bootstrap?: {Tooltip: {new(el: Element, opts?: object): {dispose(): void}}}}} */ (
      window
    ).bootstrap?.Tooltip;
    if (!Tooltip) return;
    /** @type {{dispose: Function}[]} */
    this._tooltipInstances = [];
    const icons = this.renderRoot?.querySelectorAll('[data-bs-toggle="tooltip"]') || [];
    icons.forEach((el) => {
      this._tooltipInstances.push(new Tooltip(el, { container: 'body' }));
    });
  }

  _disposeTooltips() {
    if (this._tooltipInstances) {
      this._tooltipInstances.forEach((t) => {
        t.dispose();
      });
      this._tooltipInstances = undefined;
    }
  }

  /** @param {string} helpId */
  _dismissHelp(helpId) {
    const state = this._help[helpId];
    if (!state) return;
    if (state.dontShowAgain) {
      setHelpMessageDismissed(helpId, true);
      this._help = { ...this._help, [helpId]: { ...state, dismissed: true } };
    } else {
      // Per-game suppression survives reload but a different gameId
      // (a new game) gets a fresh suppression key, so the banner
      // returns there. Persistent suppression across all games
      // requires the "don't show again" checkbox.
      if (this.gameId) suppressHelpMessageInGame(this.gameId, helpId);
      this._help = { ...this._help, [helpId]: { ...state, hidden: true } };
    }
  }

  /** @param {string} helpId @param {boolean} checked */
  _setDontShowAgain(helpId, checked) {
    const state = this._help[helpId];
    if (!state) return;
    this._help = { ...this._help, [helpId]: { ...state, dontShowAgain: checked } };
  }

  _budget() {
    return this.character?.equipmentBudget;
  }

  /**
   * Find which inventory item is currently equipped in the given slot, if any.
   * @param {string} slot
   * @returns {{ item: any, index: number } | null}
   */
  _itemInSlot(slot) {
    const inv = this.character?.inventory ?? [];
    for (let i = 0; i < inv.length; i++) {
      if (inv[i].equipped && inv[i].slot === slot) return { item: inv[i], index: i };
    }
    return null;
  }

  /** @param {any} item */
  _itemAcceptsSlot(item, slot) {
    return itemAcceptsSlot(item, slot);
  }

  /**
   * @param {DragEvent} e
   * @param {number} index
   */
  _handleCardDragStart(e, index) {
    if (this.pendingSwap) {
      e.preventDefault();
      return;
    }
    this._dragSource = { kind: 'inventory', index };
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(index));
    }
  }

  _handleCardDragEnd() {
    this._dragSource = null;
    this._hoverSlot = null;
  }

  /**
   * Drag source for an equipped slot — kicks off the unequip flow. Only
   * fires for occupied slots (the renderer sets draggable=false on empty
   * slots). The user drops onto the inventory pane to unequip.
   * @param {DragEvent} e
   * @param {string} slot
   */
  _handleSlotDragStart(e, slot) {
    if (this.pendingSwap) {
      e.preventDefault();
      return;
    }
    const equipped = this._itemInSlot(slot);
    if (!equipped) {
      e.preventDefault();
      return;
    }
    this._dragSource = {
      kind: 'slot',
      slot,
      equipmentId: equipped.item.equipmentId,
      itemIndex: equipped.index,
    };
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', `slot:${slot}`);
    }
  }

  _handleSlotDragEnd() {
    this._dragSource = null;
    this._hoverSlot = null;
  }

  /**
   * @param {DragEvent} e
   * @param {string} slot
   */
  _handleSlotDragOver(e, slot) {
    // Slots are valid drop targets only for inventory-sourced drags.
    if (this._dragSource?.kind !== 'inventory') return;
    const item = this.character?.inventory?.[this._dragSource.index];
    if (!this._itemAcceptsSlot(item, slot)) {
      this._hoverSlot = `__invalid:${slot}`;
      return;
    }
    // Combat with every tier spent — refuse the drop so the browser
    // snaps the card back, and let the slots-blocked overlay surface
    // the rule.
    if (this.combatTime && noEquipmentChangesLeft(this._budget())) {
      return;
    }
    // Donning armor takes minutes (D&D 2024 SRD), same rule as doffing.
    // Block any inventory drop on the armor slot during combat; the
    // slots-blocked overlay surfaces the rule when the item has no
    // other legal slot.
    if (slot === 'armor' && this.combatTime) {
      return;
    }
    // In combat, only empty slots accept a drop. The player must
    // unequip the current occupant first (an Object Interaction in its
    // own right). The drag overlay surfaces this with a "Not Empty"
    // caption — keep this gate in sync so the browser also snap-backs.
    if (this.combatTime) {
      if (this._itemInSlot(slot)) return;
      if (slot === 'offHand' && this._itemInSlot('mainHand')?.item?.twoHanded) return;
    }
    e.preventDefault();
    this._hoverSlot = slot;
  }

  _handleSlotDragLeave() {
    this._hoverSlot = null;
  }

  /**
   * @param {DragEvent} e
   * @param {string} slot
   */
  _handleSlotDrop(e, slot) {
    e.preventDefault();
    const src = this._dragSource;
    this._hoverSlot = null;
    this._dragSource = null;
    if (src?.kind !== 'inventory') return;

    const item = this.character?.inventory?.[src.index];
    if (!this._itemAcceptsSlot(item, slot)) {
      this._rejectInventoryCard(src.index);
      return;
    }

    const decision = decideEquipPath(this._budget());
    if (decision.kind === 'reject') {
      this._rejectInventoryCard(src.index);
      return;
    }

    if (decision.kind === 'commit') {
      this._submitEquipChange(src.index, slot, decision.tier, true);
      return;
    }

    const replaced = this._itemInSlot(slot);
    this._equipDecision = {
      slot,
      itemIndex: src.index,
      equipmentId: item.equipmentId,
      equip: true,
      replacedItemIndex: replaced?.index ?? null,
      replacedEquipmentId: replaced?.item?.equipmentId ?? null,
    };
  }

  /**
   * Drop target for unequip drags: the inventory pane accepts slot-sourced
   * drags. Mirrors the equip flow — same decision modal, just with
   * `equip: false`.
   *
   * For armor-during-combat we deliberately do NOT preventDefault, which
   * makes the OS reject the drop. Browsers then show the "no drop"
   * cursor and animate the drag image back to its source on dragend —
   * giving us the snap-back-on-rejection feedback for free.
   */
  _handleInventoryPaneDragOver(e) {
    if (this._dragSource?.kind !== 'slot') return;
    if (this._dragSource.slot === 'armor' && this.combatTime) return;
    if (this._dragSource.slot !== 'armor' && this.combatTime && noEquipmentChangesLeft(this._budget())) {
      return;
    }
    e.preventDefault();
  }

  _handleInventoryPaneDrop(e) {
    if (this._dragSource?.kind !== 'slot') return;
    e.preventDefault();
    const src = this._dragSource;
    this._dragSource = null;

    // Doffing armor is a multi-minute action (10 min for chain mail,
    // etc.) — can't happen during combat. Block the drop with no API
    // call; the drop-zone renderer surfaces the rule.
    if (src.slot === 'armor' && this.combatTime) {
      return;
    }

    const decision = decideEquipPath(this._budget());
    if (decision.kind === 'reject') {
      this._rejectInventoryCard(src.itemIndex);
      return;
    }

    if (decision.kind === 'commit') {
      this._submitEquipChange(src.itemIndex, src.slot, decision.tier, false);
      return;
    }

    // Render the confirm inline in the inventory pane instead of as a
    // modal. _unequipConfirm is the ad-hoc state the inline drop-zone
    // reads to switch from "drag here to unequip" hint to the tier-
    // choice confirm.
    this._unequipConfirm = {
      slot: src.slot,
      itemIndex: src.itemIndex,
      equipmentId: src.equipmentId,
    };
  }

  _handleCancelUnequipConfirm() {
    this._unequipConfirm = null;
  }

  /** @param {string} tier */
  _handleUnequipConfirmAlternate(tier) {
    const c = this._unequipConfirm;
    if (!c) return;
    this._unequipConfirm = null;
    this._submitEquipChange(c.itemIndex, c.slot, tier, false);
  }

  _handleUnequipConfirmAttack() {
    const c = this._unequipConfirm;
    if (!c) return;
    this._unequipConfirm = null;
    const changes = [{ equipmentId: c.equipmentId, slot: c.slot, equip: false }];
    // Open the picker immediately so the "Attack, then unequip" section
    // (current actions) is usable straight away. Kick off the preview
    // for the "Unequip, then attack" section async — section renders
    // "Loading…" while we wait, then populates.
    this._attackPicker = { changes, previewCharacter: null, previewError: null };
    this._fetchPreviewCharacter(this.gameId, this.character?.id, changes).then((result) => {
      // Bail if the picker was dismissed or replaced before preview returned.
      if (this._attackPicker?.changes !== changes) return;
      this._attackPicker = { ...this._attackPicker, ...result };
    });
  }

  /**
   * Calls the server preview-actions endpoint with the given hypothetical
   * equipment changes and returns either a populated `previewCharacter`
   * record (actions, targetSquares, equipmentBudget, ac, …) or a short
   * error string. Rendering handles both branches.
   *
   * @param {string} gameId
   * @param {string} characterId
   * @param {Array<{equipmentId?: string, slot: string, equip: boolean}>} equipmentChanges
   * @returns {Promise<{previewCharacter: any | null, previewError: string | null}>}
   */
  async _fetchPreviewCharacter(gameId, characterId, equipmentChanges) {
    if (!gameId || !characterId) {
      return { previewCharacter: null, previewError: 'Missing game or character id' };
    }
    try {
      const resp = await api.post(
        `/api/games/${encodeURIComponent(gameId)}/characters/${encodeURIComponent(characterId)}/preview-actions`,
        { equipmentChanges },
      );
      return { previewCharacter: resp?.character ?? null, previewError: null };
    } catch (err) {
      return {
        previewCharacter: null,
        previewError: err instanceof Error ? err.message : String(err),
      };
    }
  }

  /** @param {number} sourceIndex */
  _rejectInventoryCard(sourceIndex) {
    this._shakeIndex = sourceIndex;
    setTimeout(() => {
      this._shakeIndex = null;
    }, 400);
  }

  /**
   * Submit a single equip OR unequip change with the chosen tier.
   * @param {number} itemIndex
   * @param {string} slot
   * @param {string} tier
   * @param {boolean} equip
   */
  _submitEquipChange(itemIndex, slot, tier, equip) {
    const item = this.character?.inventory?.[itemIndex];
    this.dispatchEvent(
      new CustomEvent('equip-item', {
        detail: {
          characterId: this.character.id,
          equipmentId: item?.equipmentId,
          slot,
          equip,
          tier,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Build the change list for the staged Attack-bundled swap. For an
   * equip into an occupied slot we emit unequip-of-replaced + equip-of-new;
   * for a bare unequip we emit just one change with equip:false.
   */
  _stagedChanges() {
    const choice = this._equipDecision;
    if (!choice) return [];
    if (!choice.equip) {
      return [{ equipmentId: choice.equipmentId, slot: choice.slot, equip: false }];
    }
    /** @type {EquipmentChange[]} */
    const changes = [];
    if (choice.replacedEquipmentId) {
      changes.push({
        equipmentId: choice.replacedEquipmentId,
        slot: choice.slot,
        equip: false,
      });
    }
    changes.push({
      equipmentId: choice.equipmentId,
      slot: choice.slot,
      equip: true,
    });
    return changes;
  }

  _handleEquipWithAttack() {
    const changes = this._stagedChanges();
    this._equipDecision = null;
    this._attackPicker = { changes, previewCharacter: null, previewError: null };
    this._fetchPreviewCharacter(this.gameId, this.character?.id, changes).then((result) => {
      if (this._attackPicker?.changes !== changes) return;
      this._attackPicker = { ...this._attackPicker, ...result };
    });
  }

  /** @param {string} tier */
  _handleEquipWithAlternate(tier) {
    const choice = this._equipDecision;
    if (!choice) return;
    this._equipDecision = null;
    this._submitEquipChange(choice.itemIndex, choice.slot, tier, choice.equip);
  }

  _handleCancelDecision() {
    this._equipDecision = null;
  }

  /**
   * Click handler when the player picks an attack to bundle the swap with.
   * `phase` distinguishes the two SRD branches:
   *   - 'pre'  → the swap is applied BEFORE the attack (the attack uses
   *              whatever is equipped after the swap, including unarmed
   *              strike if the swap left the PC weaponless)
   *   - 'post' → the swap is applied AFTER the attack (the attack uses
   *              the currently-equipped weapon; the swap follows)
   * @param {any} action
   * @param {'pre'|'post'} phase
   */
  _handleAttackPicked(action, phase) {
    const picker = this._attackPicker;
    if (!picker) return;
    // 'pre' = the swap is applied BEFORE the attack, so the attack runs
    // against the post-swap character. Pass the previewed character record
    // along so game-map can overlay it as `myChar` while targeting (#394:
    // ranged-after-swap targeting must use the post-swap range / target
    // squares, not the pre-swap melee reach).
    // 'post' = the swap is applied AFTER the attack — the live character
    // is correct for targeting; no overlay needed.
    const previewCharacter = phase === 'pre' ? picker.previewCharacter : null;
    this._attackPicker = null;
    this.dispatchEvent(
      new CustomEvent('request-pending-swap-and-attack', {
        detail: {
          characterId: this.character.id,
          changes: picker.changes,
          action,
          phase,
          previewCharacter,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  _handleCancelAttackPicker() {
    this._attackPicker = null;
  }

  _handleCancelPendingSwap() {
    this.dispatchEvent(new CustomEvent('cancel-pending-swap', { bubbles: true, composed: true }));
  }

  _close() {
    this.dispatchEvent(new CustomEvent('close-equipment-screen', { bubbles: true, composed: true }));
  }

  /**
   * Top-of-pane info block rendered inside .silhouette-pane. Three states:
   *   - exploration (out of combat) → "swaps free" pill
   *   - my turn in combat → cost explainer + chip row showing what's
   *     still available
   *   - other player's turn in combat → "wait for your turn" notice
   * Returns '' when there's no character data yet to avoid flash on
   * first render.
   */
  /**
   * Top-of-pane stack: info card on top, vertical change-indicator pip
   * column below (only when in combat AND it's my turn). Three states
   * for the info card:
   *   - exploration → blue "no action cost" pill
   *   - my turn in combat → blue cost explainer (chips live below in
   *     the indicator column, not inside this card)
   *   - other player's turn in combat → amber "wait for your turn"
   */
  /** @param {any} b */
  _renderInfoCard(b) {
    if (b.explorationFree) {
      return html`<div class="alert alert-info mb-0">
        <div class="d-flex gap-2 align-items-start">
          <i class="bi bi-info-circle-fill flex-shrink-0 mt-1" aria-hidden="true"></i>
          <div class="flex-grow-1">
            Equipment changes do not require spending an action during exploration, outside of combat.
          </div>
        </div>
      </div>`;
    }
    if (!this.isMyTurn) {
      return html`<div class="alert alert-warning mb-0 text-center">
        You can only make equipment changes during your turn.
      </div>`;
    }
    return this._renderDismissibleHelp(
      COST_EXPLAINER_HELP_ID,
      html`<p class="mb-0">
          Equipment changes during combat are limited to one weapon change as part of your
          <strong>Attack</strong> action, one free <strong>object interaction</strong> per turn,
          and using the <strong>Utilize</strong> action.
        </p>
        <p class="mb-0">
          The indicators below show what equipment changes are still available
          (<span class="cost-indicator-pip-inline available" aria-hidden="true"></span>) or have
          been used
          (<span class="cost-indicator-pip-inline used" aria-hidden="true">✓</span>).
        </p>`,
    );
  }

  /**
   * Generic dismissible help banner. Hidden when the help state has been
   * persisted server-side ("don't show again") or suppressed for this
   * game session. Body is the inner content (typically a <p> or two);
   * the icon, footer row, checkbox, and Dismiss button are shared.
   * @param {string} helpId
   * @param {import('lit').TemplateResult} body
   */
  _renderDismissibleHelp(helpId, body) {
    const state = this._help[helpId];
    if (!state || state.dismissed || state.hidden) return '';
    return html`<div class="alert alert-info mb-0">
      <div class="d-flex gap-2 align-items-start">
        <i class="bi bi-info-circle-fill flex-shrink-0 mt-1"></i>
        <div class="flex-grow-1 d-flex flex-column gap-2">${body}</div>
      </div>
      <div class="d-flex justify-content-end align-items-center gap-3 mt-2">
        <label class="form-check small mb-0">
          <input
            class="form-check-input"
            type="checkbox"
            .checked="${state.dontShowAgain}"
            @change="${(/** @type {Event} */ e) =>
              this._setDontShowAgain(helpId, /** @type {HTMLInputElement} */ (e.target).checked)}"
          />
          <span class="form-check-label">Don't show this again</span>
        </label>
        <button
          type="button"
          class="btn btn-sm btn-outline-primary"
          @click="${() => this._dismissHelp(helpId)}"
        >
          Dismiss
        </button>
      </div>
    </div>`;
  }

  /** @param {any} b */
  _renderCostIndicators(b) {
    // Only show during the player's own combat turn — exploration and
    // other-players'-turn states aren't gated by these costs.
    if (b.explorationFree || !this.isMyTurn) return '';
    /**
     * @param {string} status
     * @param {string} label
     * @param {string} [tooltip] SRD rules text shown on info-icon hover.
     */
    const indicator = (status, label, tooltip) => {
      const cls = ['cost-indicator', status];
      const pipChar = status === 'used' ? '✓' : '';
      return html`<div class="${cls.join(' ')}">
        <span class="pip" aria-hidden="true">${pipChar}</span>
        <span class="label">${label}</span>
        ${
          tooltip
            ? html`<i
              class="bi bi-info-circle ms-1 text-secondary"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              data-bs-title="${tooltip}"
              tabindex="0"
              aria-label="More info"
            ></i>`
            : ''
        }
      </div>`;
    };
    return html`<div class="d-flex flex-column gap-2">
      ${indicator(
        b.attackBundledSwap,
        'Weapon change during Attack',
        'You can either equip or unequip one weapon when you make an attack as part of this action. You do so either before or after the attack. If you equip a weapon before an attack, you don’t need to use it for that attack. Equipping a weapon includes drawing it from a sheath or picking it up. Unequipping a weapon includes sheathing, stowing, or dropping it.',
      )}
      ${indicator(
        b.objectInteraction,
        'Object Interaction',
        'When time is short, such as in combat, interactions with objects are limited: one free interaction per turn. That interaction must occur during a creature’s movement or action. Any additional interactions require the Utilize action.',
      )}
      ${indicator(
        b.action,
        'Utilize action',
        'When time is short, such as in combat, interactions with objects are limited: one free interaction per turn. That interaction must occur during a creature’s movement or action. Any additional interactions require the Utilize action.',
      )}
    </div>`;
  }

  /** @param {string} slot */
  _renderSlot(slot) {
    const equippedHere = this._itemInSlot(slot);
    // Two-handed weapons are stored on mainHand only but visually occupy
    // both hands. When rendering offHand with no direct item, surface the
    // mainHand 2H weapon as a non-draggable "second grip" so the player
    // sees both hands are in use.
    const twoHandedGrip =
      slot === 'offHand' && !equippedHere
        ? this._itemInSlot('mainHand')?.item?.twoHanded
          ? this._itemInSlot('mainHand')
          : null
        : null;
    const displayItem = equippedHere ?? twoHandedGrip;
    const isHover = this._hoverSlot === slot;
    const isInvalid = this._hoverSlot === `__invalid:${slot}`;
    const isDragSource = this._dragSource?.kind === 'slot' && this._dragSource.slot === slot;
    // Persistent drag-eligibility hint: once a drag from inventory starts,
    // every slot tags itself eligible / blocked for the dragged item so
    // the player can see at a glance which slots will accept it without
    // having to hover each one.
    const eligibility = this._slotDragEligibility(slot);
    const cls = ['slot', displayItem ? 'occupied' : 'empty'];
    if (twoHandedGrip) cls.push('two-handed-grip');
    if (eligibility?.state === 'eligible') cls.push('drag-eligible');
    if (eligibility?.state === 'blocked') cls.push('drag-blocked');
    if (isHover) cls.push('valid-target');
    if (isInvalid) cls.push('invalid-target');
    if (isDragSource) cls.push('dragging');
    const labelMap = { mainHand: 'Main Hand', offHand: 'Off Hand', armor: 'Armor' };
    // Only the actual slot-of-record is draggable. The offHand grip is a
    // ghost — the player must drag from mainHand to unequip the 2H weapon.
    const isDraggable = !!equippedHere;
    return html`<div
      class="${cls.join(' ')}"
      data-slot="${slot}"
      draggable="${isDraggable ? 'true' : 'false'}"
      @dragstart="${(/** @type {DragEvent} */ e) => this._handleSlotDragStart(e, slot)}"
      @dragend="${this._handleSlotDragEnd}"
      @dragover="${(/** @type {DragEvent} */ e) => this._handleSlotDragOver(e, slot)}"
      @dragleave="${this._handleSlotDragLeave}"
      @drop="${(/** @type {DragEvent} */ e) => this._handleSlotDrop(e, slot)}"
    >
      <div class="slot-label">${labelMap[slot] || slot}</div>
      <div class="slot-item-name">
        ${displayItem ? displayItem.item.name : 'Empty'}
        ${twoHandedGrip ? html`<div class="slot-grip-note">(Two-Handed)</div>` : ''}
      </div>
      ${
        eligibility
          ? html`<div class="drag-overlay">
            ${this._renderDragOverlayIcon(slot, eligibility.state)}
            ${
              eligibility.state === 'blocked' && eligibility.reason
                ? html`<div class="drag-overlay-reason">${eligibility.reason}</div>`
                : ''
            }
          </div>`
          : ''
      }
    </div>`;
  }

  /**
   * Inline drop zone for unequipping. Active in two states:
   *   1. While a slot-sourced drag is in flight → "Drag here to unequip"
   *      hint with a dashed-blue drop area (mirrors the new-character
   *      flow's drag affordances).
   *   2. After the drop, when the player needs to choose a tier → the
   *      same area becomes a confirm with the same option buttons the
   *      modal used (Cancel / Equip with free interaction or Utilize
   *      action / Equip with Attack — verb swapped to "Unequip").
   * Returns '' when neither state is active so the inventory grid renders
   * normally.
   */
  /**
   * Red-X overlay covering the silhouette/slots while the player drags
   * an inventory card under a rule that makes every slot illegal.
   * Mirrors the unequip-side blocked drop-zone treatment. Two trigger
   * conditions, in order (armor wins ties — the SRD timing rule blocks
   * the drag even when the player has tiers to spend, so calling out
   * "out of changes" would be misleading):
   *   - combat with armor (item's only legal slot is body) — donning
   *     takes minutes per the SRD, same rule as doffing
   *   - combat with every equipment-change tier spent
   * Renders nothing otherwise so the slots stay normally interactive.
   */
  _renderSlotsBlockedOverlay() {
    if (this._dragSource?.kind !== 'inventory') return '';
    if (!this.combatTime) return '';
    const item = this.character?.inventory?.[this._dragSource.index];
    const slots = Array.isArray(item?.equippableSlots) ? item.equippableSlots : [];
    if (slots.length > 0 && slots.every((s) => s === 'armor')) {
      return html`<div class="slots-blocked-overlay">
        <i class="bi bi-x-circle-fill" aria-hidden="true"></i>
        <div class="title">Armor cannot be donned during combat</div>
      </div>`;
    }
    if (noEquipmentChangesLeft(this._budget())) {
      return html`<div class="slots-blocked-overlay">
        <i class="bi bi-x-circle-fill" aria-hidden="true"></i>
        <div class="title">Out of equipment changes for the turn</div>
      </div>`;
    }
    return '';
  }

  _renderUnequipDropZone() {
    const isDragging = this._dragSource?.kind === 'slot';
    const confirm = this._unequipConfirm;
    if (!isDragging && !confirm) return '';

    // Doffing armor takes minutes (D&D 2024: chain mail = 10 min, etc.)
    // — never legal during combat. Surface the rule on the drop zone so
    // the player sees why nothing happens when they drop.
    if (isDragging && this._dragSource?.kind === 'slot' && this._dragSource.slot === 'armor' && this.combatTime) {
      return html`<div class="unequip-zone blocked">
        <i class="bi bi-x-circle-fill" aria-hidden="true"></i>
        <div class="title">Armor cannot be doffed during combat</div>
      </div>`;
    }

    // Weapon slot drag in combat with every tier already spent — same
    // red-X treatment as the armor block. The dragover gate above
    // mirrors this so the browser snaps the card back.
    if (
      isDragging &&
      this._dragSource?.kind === 'slot' &&
      this._dragSource.slot !== 'armor' &&
      this.combatTime &&
      noEquipmentChangesLeft(this._budget())
    ) {
      return html`<div class="unequip-zone blocked">
        <i class="bi bi-x-circle-fill" aria-hidden="true"></i>
        <div class="title">Out of equipment changes for the turn</div>
      </div>`;
    }

    if (confirm) {
      const alt = alternateEquipOption(this._budget());
      return html`<div class="unequip-zone confirm">
        <div class="title">Unequip with which cost?</div>
        <div class="d-flex flex-wrap gap-2 justify-content-end align-items-center">
          ${
            alt
              ? html`<button
                class="btn btn-outline-secondary"
                @click="${() => this._handleUnequipConfirmAlternate(alt.tier)}"
              >
                ${alt.label.replace('Equip', 'Unequip')}
              </button>`
              : ''
          }
          <button class="btn btn-primary" @click="${this._handleUnequipConfirmAttack}">
            Unequip with Attack
          </button>
          <button class="btn btn-danger ms-auto" @click="${this._handleCancelUnequipConfirm}">
            Cancel
          </button>
        </div>
      </div>`;
    }
    return html`<div class="unequip-zone">
      <i class="bi bi-arrow-down-circle" aria-hidden="true"></i>
      <div class="title">Drag here to unequip</div>
    </div>`;
  }

  /**
   * @param {any} item
   * @param {number} index
   */
  _renderInventoryCard(item, index) {
    const cls = ['inv-card'];
    if (item.equipped) cls.push('equipped');
    if (this._dragSource?.kind === 'inventory' && this._dragSource.index === index) {
      cls.push('dragging');
    }
    if (this._shakeIndex === index) cls.push('shake');
    const slotIcon = this._renderSlotRequirementIcon(item);
    // Single-line summary under the name (when present): damage for
    // weapons, AC for armor. Quantity, category, and properties live in
    // the badge rows.
    let summary = '';
    if (item.damageDice) {
      summary = `${item.damageDice} ${item.damageType ?? ''}`.trim();
    } else if (item.acBonus != null) {
      summary = `AC ${item.acBonus}`;
    }
    const showQuantity = item.quantity != null && item.quantity > 1;
    const properties = Array.isArray(item.properties) ? item.properties : [];
    // Items with no equippableSlots (rations, rope, backpack, etc.)
    // can't go in any slot, so they're not draggable. Pending-swap
    // mode locks every drag too — the player must commit or cancel
    // the staged swap first.
    const isEquippable = Array.isArray(item.equippableSlots) && item.equippableSlots.length > 0;
    const isDraggable = isEquippable && !this.pendingSwap;
    if (!isEquippable) cls.push('not-equippable');
    return html`<div
      class="${cls.join(' ')}"
      draggable="${isDraggable ? 'true' : 'false'}"
      @dragstart="${(/** @type {DragEvent} */ e) => this._handleCardDragStart(e, index)}"
      @dragend="${this._handleCardDragEnd}"
    >
      <div class="name-row">
        <span class="name">${item.name || 'Unknown'}</span>
        ${showQuantity ? html`<span class="badge qty-badge">×${item.quantity}</span>` : ''}
        ${
          item.description
            ? html`<i
              class="bi bi-info-circle item-info-icon"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              data-bs-title="${item.description}"
              tabindex="0"
              aria-label="Item description"
            ></i>`
            : ''
        }
      </div>
      ${summary ? html`<div class="summary">${summary}</div>` : ''}
      ${
        item.category || properties.length > 0 || item.minStr > 0
          ? html`<div class="badge-row">
            ${
              item.category
                ? html`<span class="badge text-bg-secondary category-badge"
                  >${this._categoryLabel(item.category)}</span
                >`
                : ''
            }
            ${properties.map(
              (/** @type {string} */ p) =>
                html`<span
                  class="badge text-bg-light text-dark border property-badge"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  data-bs-title="${this._propertyTooltip(p)}"
                  tabindex="0"
                  >${this._propertyLabel(p)}</span
                >`,
            )}
            ${
              item.minStr > 0
                ? html`<span
                  class="badge text-bg-light text-dark border property-badge"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  data-bs-title="If your Strength score is below ${item.minStr}, your speed is reduced by 10 feet while wearing this armor."
                  tabindex="0"
                  >Str ${item.minStr}</span
                >`
                : ''
            }
          </div>`
          : ''
      }
      ${slotIcon ? html`<div class="slot-icons">${slotIcon}</div>` : ''}
    </div>`;
  }

  /**
   * Display label for an equipment-category slug ("weapon" → "Weapon",
   * "adventuring-gear" → "Adventuring Gear", etc.). Falls back to the
   * raw slug when an unknown category arrives so missing entries are
   * obvious rather than silently rendering blank.
   * @param {string} slug
   */
  _categoryLabel(slug) {
    /** @type {Record<string, string>} */
    const labels = {
      weapon: 'Weapon',
      armor: 'Armor',
      'adventuring-gear': 'Adventuring Gear',
    };
    return labels[slug] ?? slug;
  }

  /**
   * Format an SRD weapon- or armor-property slug for badge display.
   * @param {string} slug
   */
  _propertyLabel(slug) {
    /** @type {Record<string, string>} */
    const labels = {
      // Weapon
      finesse: 'Finesse',
      heavy: 'Heavy',
      light: 'Light',
      loading: 'Loading',
      reach: 'Reach',
      thrown: 'Thrown',
      twoHanded: 'Two-Handed',
      versatile: 'Versatile',
      ammunition: 'Ammunition',
      // Armor — kept terse for badge layout; tooltip carries the
      // "Armor" qualifier and the rule text.
      lightArmor: 'Light',
      mediumArmor: 'Medium',
      heavyArmor: 'Heavy',
      shield: 'Shield',
      stealthDisadvantage: 'Stealth Dis.',
    };
    return labels[slug] ?? slug;
  }

  /**
   * Hover/focus tooltip text for a property badge — short paraphrase of
   * the SRD rule the property invokes. Returns the slug unchanged if we
   * don't have a copy line for it (so a missed entry is at least
   * visible rather than a silent empty tooltip).
   * @param {string} slug
   */
  _propertyTooltip(slug) {
    /** @type {Record<string, string>} */
    const tooltips = {
      finesse:
        'When attacking with a Finesse weapon, you can use your Strength or Dexterity modifier for the attack and damage rolls.',
      heavy: 'Small creatures have disadvantage on attack rolls with a Heavy weapon.',
      light: 'A Light weapon is small and easy to handle, ideal for use when fighting with two weapons.',
      loading:
        'Because of the time required to load a Loading weapon, you can fire only one piece of ammunition with it when you use an action, a Bonus Action, or a Reaction.',
      reach: 'A Reach weapon adds 5 feet to your reach when you attack with it.',
      thrown:
        'You can throw a Thrown weapon to make a ranged attack, using the same ability modifier as for a melee attack.',
      twoHanded: 'A Two-Handed weapon requires two hands to attack with.',
      versatile:
        'A Versatile weapon can be used with one or two hands. The two-handed damage is shown in parentheses after the normal damage.',
      ammunition:
        'A weapon with the Ammunition property requires ammunition to make a ranged attack. You spend one piece of ammunition each time you make a ranged attack with it.',
      lightArmor: "Light armor doesn't restrict movement; you add your full Dexterity modifier to your AC.",
      mediumArmor: 'Medium armor lets you add your Dexterity modifier (max +2) to your AC.',
      heavyArmor: "Heavy armor uses a fixed AC and doesn't add your Dexterity modifier.",
      shield: 'Wielding a Shield in one hand grants +2 to your AC.',
      stealthDisadvantage: 'The wearer has disadvantage on Dexterity (Stealth) checks.',
    };
    return tooltips[slug] ?? slug;
  }

  /**
   * Picks the overlay glyph painted on top of a slot during an inventory
   * drag. Eligible slots get the icon of what would land there (armor /
   * one-hand / two-hand). Blocked slots get a red X.
   * @param {string} slot
   * @param {'eligible' | 'blocked'} eligibility
   */
  _renderDragOverlayIcon(slot, eligibility) {
    if (eligibility === 'blocked') {
      return html`<i class="bi bi-x-circle-fill" aria-hidden="true"></i>`;
    }
    if (slot === 'armor') return renderArmorSlotIcon();
    const item = this._dragSource?.kind === 'inventory' ? this.character?.inventory?.[this._dragSource.index] : null;
    if (item?.twoHanded) return renderTwoHandSlotIcon();
    return renderHandSlotIcon();
  }

  /**
   * Drag-eligibility for a slot during an inventory drag. Returns:
   *   - { state: 'eligible' }              — the item can land here.
   *   - { state: 'blocked', reason? }      — the item cannot land here.
   *     `reason`, if set, is a short uppercase caption rendered under
   *     the red X (e.g. "Not Empty" when combat forbids displacing).
   *   - null                               — no drag in progress, or
   *     drag source isn't from inventory (e.g. unequip drag from a
   *     slot).
   * Used by _renderSlot to apply a persistent visual highlight to every
   * slot for the duration of the drag, not just when it's hovered.
   * @param {string} slot
   * @returns {{ state: 'eligible' } | { state: 'blocked', reason?: string } | null}
   */
  _slotDragEligibility(slot) {
    if (this._dragSource?.kind !== 'inventory') return null;
    const item = this.character?.inventory?.[this._dragSource.index];
    if (!item) return null;
    if (!this._itemAcceptsSlot(item, slot)) return { state: 'blocked' };
    if (slot === 'armor' && this.combatTime) return { state: 'blocked' };
    if (this.combatTime && noEquipmentChangesLeft(this._budget())) {
      return { state: 'blocked' };
    }
    // Combat rule: a drop only commits if the slot is empty. Players must
    // unequip the current occupant first (an Object Interaction in its
    // own right). Out of combat, the equip flow auto-displaces.
    if (this.combatTime) {
      if (this._itemInSlot(slot)) return { state: 'blocked', reason: 'Not Empty' };
      if (slot === 'offHand' && this._itemInSlot('mainHand')?.item?.twoHanded) {
        return { state: 'blocked', reason: 'Not Empty' };
      }
    }
    return { state: 'eligible' };
  }

  /**
   * Picks the slot-requirement glyph for an inventory card. Items that
   * aren't equippable get nothing. Armor wins over hand icons; two-handed
   * weapons get the two-hand glyph; anything else equippable to a hand
   * slot gets the single-hand glyph.
   * @param {any} item
   */
  _renderSlotRequirementIcon(item) {
    const slots = Array.isArray(item?.equippableSlots) ? item.equippableSlots : [];
    if (slots.length === 0) return null;
    if (slots.includes('armor')) return renderArmorSlotIcon();
    if (item?.twoHanded) return renderTwoHandSlotIcon();
    if (slots.includes('mainHand') || slots.includes('offHand')) return renderHandSlotIcon();
    return null;
  }

  _renderEquipDecisionModal() {
    if (!this._equipDecision) return '';
    const alt = alternateEquipOption(this._budget());
    return html`<div
      class="sub-modal-backdrop"
      @click="${(/** @type {Event} */ e) => {
        if (e.target === e.currentTarget) this._handleCancelDecision();
      }}"
    >
      <div class="sub-modal" @click="${(/** @type {Event} */ e) => e.stopPropagation()}">
        <h3 class="sub-modal-title">Equip with Attack?</h3>
        <p class="sub-modal-body">
          You can either equip or unequip one weapon when you make an attack as part of the
          action. You do so either before or after the attack. If you equip a weapon before an
          attack, you don't need to use it for that attack.
        </p>
        <div class="sub-modal-actions">
          <button class="btn btn-link text-secondary" @click="${this._handleCancelDecision}">Cancel</button>
          ${
            alt
              ? html`<button class="btn btn-outline-secondary" @click="${() => this._handleEquipWithAlternate(alt.tier)}">
                ${alt.label}
              </button>`
              : ''
          }
          <button class="btn btn-primary" @click="${this._handleEquipWithAttack}">
            Equip with Attack
          </button>
        </div>
      </div>
    </div>`;
  }

  _renderAttackPicker() {
    const picker = this._attackPicker;
    if (!picker) return '';

    // Two branches:
    //   1. "Attack, then unequip" — current actions; the swap rides along
    //      as postfix (applied after the attack resolves).
    //   2. "Unequip, then attack" — preview actions (server-computed);
    //      the swap rides along as prefix (applied before the attack).
    // Both branches share the same `changes` payload and the same atomic
    // commit guarantee.
    const currentActions = (this.character?.actions ?? []).filter(
      (/** @type {any} */ a) => a.actionType === 'attack' && a.enabled !== false,
    );
    const previewActions = picker.previewCharacter?.actions
      ? picker.previewCharacter.actions.filter(
          (/** @type {any} */ a) => a.actionType === 'attack' && a.enabled !== false,
        )
      : null;

    // Verb agreement on the section headers and copy. If every staged
    // change is an unequip (single weapon dropped from a slot), use
    // "unequip"; otherwise use the more general "swap" since the change
    // list can replace one weapon with another.
    const allUnequips = picker.changes.every((c) => c.equip === false);
    const verb = allUnequips ? 'unequip' : 'swap';

    /**
     * @param {any} a
     * @param {'pre'|'post'} phase
     */
    const renderActionButton = (a, phase) => html`<button
      class="list-group-item list-group-item-action"
      @click="${() => this._handleAttackPicked(a, phase)}"
    >
      <div class="fw-bold">${a.name}</div>
      ${a.actionSummary ? html`<small class="text-muted">${a.actionSummary}</small>` : ''}
    </button>`;

    return html`<div
      class="sub-modal-backdrop"
      @click="${(/** @type {Event} */ e) => {
        if (e.target === e.currentTarget) this._handleCancelAttackPicker();
      }}"
    >
      <div class="sub-modal" @click="${(/** @type {Event} */ e) => e.stopPropagation()}">
        <h3 class="sub-modal-title">Choose an attack</h3>
        <p class="sub-modal-body">
          You can swap before or after your attack. Pick which branch you want — the swap and
          the attack are submitted together; neither happens unless both succeed.
        </p>

        <h6 class="mb-1">Attack, then ${verb}</h6>
        ${
          currentActions.length === 0
            ? html`<div class="empty-text small">No attack actions currently available.</div>`
            : html`<div class="list-group attack-list">
              ${currentActions.map((a) => renderActionButton(a, 'post'))}
            </div>`
        }

        <h6 class="mb-1 mt-2">${verb[0].toUpperCase() + verb.slice(1)}, then attack</h6>
        ${
          previewActions === null
            ? picker.previewError
              ? html`<div class="empty-text small text-danger">
                Couldn't preview post-${verb} actions: ${picker.previewError}
              </div>`
              : html`<div class="empty-text small">Loading…</div>`
            : previewActions.length === 0
              ? html`<div class="empty-text small">No attack actions after the ${verb}.</div>`
              : html`<div class="list-group attack-list">
                ${previewActions.map((a) => renderActionButton(a, 'pre'))}
              </div>`
        }

        <div class="sub-modal-actions">
          <button class="btn btn-link text-secondary" @click="${this._handleCancelAttackPicker}">Cancel</button>
        </div>
      </div>
    </div>`;
  }

  render() {
    if (!this.character) return html``;
    const inventory = this.character.inventory ?? [];
    // Display alphabetically by name, but keep each item's original index
    // so drag/drop handlers can still address the inventory slot by
    // position. (server snapshots aren't sorted; this is a view-layer
    // ordering only.)
    const sortedInventory = inventory
      .map((/** @type {any} */ item, /** @type {number} */ originalIndex) => ({ item, originalIndex }))
      .sort((/** @type {any} */ a, /** @type {any} */ b) => (a.item.name || '').localeCompare(b.item.name || ''));
    const b = this.character.equipmentBudget;
    return html`
      <app-viewport heading="Equipment Manager" no-padding @close="${this._close}">
        <span slot="heading-icon">${renderBackpackIcon()}</span>
        ${
          this.pendingSwap
            ? html`<div class="alert alert-warning d-flex align-items-center gap-3 mb-0 rounded-0">
              <span>Swap pending — make an attack to commit, or cancel.</span>
              <button class="btn btn-sm btn-outline-warning ms-auto" @click="${this._handleCancelPendingSwap}">
                Cancel
              </button>
            </div>`
            : ''
        }
        <div class="page row g-0 flex-grow-1 flex-nowrap">
          <div class="col-md-5 col-xl-4 pane left">
            <section class="d-flex flex-column gap-2">
              <h3 class="subheader">Equipment Changes</h3>
              ${b ? this._renderInfoCard(b) : ''} ${b ? this._renderCostIndicators(b) : ''}
            </section>

            <section class="d-flex flex-column gap-2">
              <h3 class="subheader">Equipment Slots</h3>
              <div class="slots-grid">
                <div class="silhouette-col">
                  <div class="silhouette-figure">${renderAdventurerSilhouette()}</div>
                </div>
                ${this._renderSlot('armor')}
                ${this._renderSlot('mainHand')}
                ${this._renderSlot('offHand')}
                ${this._renderSlotsBlockedOverlay()}
              </div>
            </section>
          </div>

          <div
            class="col-md-7 col-xl-8 pane"
            @dragover="${this._handleInventoryPaneDragOver}"
            @drop="${this._handleInventoryPaneDrop}"
          >
            <h3 class="subheader">My Equipment</h3>
            ${this._renderDismissibleHelp(
              DRAG_INSTRUCTION_HELP_ID,
              html`<p class="mb-0">
                To equip an item, drag it from the list below into a slot on the silhouette
                the left. To unequip, drag the item out of a slot.
              </p>`,
            )}
            ${
              sortedInventory.length === 0
                ? html`<div class="empty-text">No items in inventory.</div>`
                : html`<div class="inventory-grid">
                  ${sortedInventory.map(({ item, originalIndex }) => this._renderInventoryCard(item, originalIndex))}
                </div>`
            }
            ${this._renderUnequipDropZone()}
          </div>
        </div>
      </app-viewport>
      ${this._renderEquipDecisionModal()} ${this._renderAttackPicker()}
    `;
  }
}

if (!customElements.get('equipment-screen')) {
  customElements.define('equipment-screen', EquipmentScreen);
}

export { EquipmentScreen };
